import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddPurchaseOrder() {
  const [currentUser, setCurrentUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split("T")[0]);
  const [supplier, setSupplier] = useState(null);
  const [supplierName, setSupplierName] = useState(null);
  const [suppliersList, setSuppliersList] = useState([]);
  const [items, setItems] = useState([{ product_id: "", quantity: 1, unit_price: 0, total_price: 0 }]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {

    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in.");
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/users/specific`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const userData = response.data;
        setCurrentUser(userData);

        // Fetch locations and products based on role
        if (userData.role === "Main Admin") {
          fetchAllLocations();
          //fetchAllProducts();
        } else if (userData.role === "Company Admin" || userData.role === "Farm User" || userData.role === "Outlet User") {
          fetchFilteredLocations(userData);
          fetchFilteredProducts(userData);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }
    };

    const fetchAllLocations = async () => {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `${API_BASE_URL}/locations`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setLocations(response.data.locations);
        };
    
    const fetchFilteredProducts = async (userData) => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/products/filtered-products?organization=${userData.organization._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProducts(response.data.products);
    };

    const fetchFilteredLocations = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/products/filtered-locations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setLocations(response.data.locations);
    };

    const fetchSuppliers = async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/suppliers/get`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSuppliersList(response.data.suppliers);
    };

    fetchSuppliers();
    fetchCurrentUser();
  }, []);

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
  
    if (field === "product_id") {
      updatedItems[index].product_id = value;
  
      // Autofill unit price if product exists
      const selectedProduct = products.find((p) => p._id === value);
      if (selectedProduct) {
        updatedItems[index].unit_price = parseFloat(selectedProduct.purchase_price || 0);
        const quantity = parseFloat(updatedItems[index].quantity || 1);
        updatedItems[index].total_price = quantity * updatedItems[index].unit_price;
      }
    } else if (field === "quantity" || field === "unit_price") {
      updatedItems[index][field] = parseFloat(value);
      const quantity = parseFloat(updatedItems[index].quantity || 0);
      const unit_price = parseFloat(updatedItems[index].unit_price || 0);
      updatedItems[index].total_price = quantity * unit_price;
    } else {
      updatedItems[index][field] = value;
    }
  
    setItems(updatedItems);
    updateTotalAmount(updatedItems);
  };

  const ProductOption = ({ data, innerRef, innerProps, isFocused, isSelected }) => {
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={`flex items-center p-2 space-x-2 rounded ${
          isFocused ? "bg-gray-200" : ""
        } ${isSelected ? "bg-green-600 text-white" : ""}`}
      >
        {data.product_image && (
          <img
            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${data.product_image}`}
            alt={data.product_name}
            className="w-8 h-8 object-cover rounded"
          />
        )}
        <span>{data.product_name}</span>
      </div>
    );
  };
  
  

  const customProductSelect = (index) => {
    const selectedProductId = items[index].product_id;
  
    const filteredOptions = products.filter(
      (product) =>
        !items.some((item, i) => i !== index && item.product_id === product._id)
    );
  
    return (
      <Select
        options={filteredOptions}
        getOptionLabel={(e) => e.product_name}
        getOptionValue={(e) => e._id}
        isSearchable
        placeholder="Search and select product"
        className="w-full"
        components={{ Option: ProductOption }}
        value={filteredOptions.find((option) => option._id === selectedProductId) || null}
        onChange={(selectedOption) => {
          handleItemChange(index, "product_id", selectedOption._id);
          // Optional: set unit, if needed
        }}
      />
    );
  };
   

  const handleAddItem = () => {
    setItems([...items, { product_id: "", quantity: 1, unit_price: 0, total_price: 0 }]);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    updateTotalAmount(newItems);
  };

  const updateTotalAmount = (itemsList) => {
    const total = itemsList.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
    setTotalAmount(total);
  };

  const handleSubmit = async () => {
    if (!supplier || items.length === 0 || !orderDate) {
      return toast.error("Please fill in all required fields.");
    }

    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const formData = {
      supplier_id: supplier._id,
      order_date: orderDate,
      items: items.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
      })),
      total_amount: totalAmount,
    };

    try {
      await axios.post(`${API_BASE_URL}/purchaseOrders/add`, formData, { headers });
      toast.success("Purchase order created successfully!");
      setItems([]);
      setTotalAmount(0);
      setOrderDate(new Date().toISOString().split("T")[0]);
      setSupplier(null);
    } catch (error) {
      toast.error("Failed to create purchase order.");
    }
  };

  // React Select Components
  const customSupplierIDSelect = () => (
    <Select
      options={suppliersList}
      getOptionLabel={(e) => e.supplier_code}
      getOptionValue={(e) => e._id}
      isSearchable
      placeholder="Search by Supplier ID"
      className="w-full"
      value={suppliersList.find((option) => option._id === supplier?._id) || null}
      onChange={(selectedOption) => {
        setSupplier(selectedOption);
        setSupplierName(selectedOption?.supplier_name);
      }}
    />
  );

  const customSupplierNameSelect = () => (
    <Select
      options={suppliersList}
      getOptionLabel={(e) => e.supplier_name}
      getOptionValue={(e) => e._id}
      isSearchable
      placeholder="Search by Supplier Name"
      className="w-full"
      value={suppliersList.find((option) => option.supplier_name === supplierName) || null}
      onChange={(selectedOption) => {
        setSupplier(selectedOption);
        setSupplierName(selectedOption?.supplier_name);
      }}
    />
  );

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Add Purchase Order</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-gray-700 mb-2">Date</label>
            <input
              type="date"
              className="w-full p-3 border rounded-lg"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Supplier ID</label>
            {customSupplierIDSelect()}
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Supplier Name</label>
            {customSupplierNameSelect()}
          </div>
        </div>

        <h3 className="text-lg font-semibold mb-4">Order Items</h3>

        {/* Column Headers */}
        <div className="grid grid-cols-2 gap-8 mb-1 block text-gray-700 ">
          <div>Product</div>
          <div className="grid grid-cols-4 gap-8 mb-2 block text-gray-700">
            <div>Quantity</div>
            <div>Unit Price</div>
            <div>Price</div>
            <div>Action</div>
          </div>  
        </div>

        {items.map((item, index) => (
          <div key={index} className="grid grid-cols-2 gap-8 mb-4">
            <div className="w-full">
              {customProductSelect(index)}
            </div>
            <div className="grid grid-cols-4 gap-8 mb-4">
              <input
                type="number"
                placeholder="Quantity"
                value={item.quantity}
                onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                className="col-span-1 p-2 border rounded"
              />
              <input
                type="number"
                placeholder="Unit Price"
                value={item.unit_price}
                onChange={(e) => handleItemChange(index, "unit_price", e.target.value)}
                className="col-span-1 p-2 border rounded"
              />
              <input
                type="number"
                placeholder="Total Price"
                onChange={(e) => handleItemChange(index, "total_price", e.target.value)}
                value={item.total_price}
                readOnly
                className="col-span-1 p-2 border bg-gray-100"
              />
              <div className="w-full flex items-center">
                <button
                  onClick={() => handleRemoveItem(index)}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={handleAddItem}
          className="mb-4 px-4 py-2 bg-green-600 text-white rounded"
        >
          Add Item
        </button>

        <div className="mt-4 text-right font-semibold text-xl text-green-800">
          Total: Rs. {totalAmount.toFixed(2)}
        </div>

        <div className="mt-6 text-right">
          <button
            onClick={handleSubmit}
            className="px-6 py-3 bg-green-700 text-white rounded-lg hover:bg-green-800"
          >
            Submit Purchase Order
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddPurchaseOrder;
